/* eslint-disable max-len */
// src/components/NewProject.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from "firebase/functions";
import { serverTimestamp, doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { functions, db, storage, auth } from '../../firebaseConfig';
import { FiRefreshCw, FiInfo, FiAlertTriangle, FiUploadCloud, FiTag, FiList, FiCpu } from 'react-icons/fi';
import { INDUSTRIES } from '../shared/constants/industries';

import { useToast } from '../shared/contexts/ToastContext';

import ReactCrop, { centerCrop, makeAspectCrop, convertToPixelCrop } from 'react-image-crop';
import 'react-image-crop/src/ReactCrop.scss';

// --- Constants ---
const platforms = ["Meta (Facebook & Instagram)"];
const tones = ["Professional", "Friendly", "Inspirational", "Playful", "Bold", "Casual", "Authoritative", "Witty"];


const MIN_KEYWORDS = 3;
const MAX_KEYWORDS = 10;
const MIN_SHORT_DESC_LENGTH = 30;
const MAX_SHORT_DESC_LENGTH = 250;
const PROJECT_NAME_MAX_LENGTH = 100;
const ASPECT_RATIO = 16 / 9;
const MIN_DIMENSION = 150;


function NewProject() {
    const navigate = useNavigate();
    const { addToast } = useToast();
    const userId = auth.currentUser?.uid;

    // --- State for User Inputs ---
    const [platform, setPlatform] = useState(platforms[0]);
    const [industry, setIndustry] = useState('');
    const [industryQuery, setIndustryQuery] = useState('');
    const [industryOpen, setIndustryOpen] = useState(false);
    const industryRef = useRef(null);

    const [keywordsInput, setKeywordsInput] = useState('');
    const [userShortDescription, setUserShortDescription] = useState('');
    const [brandVoiceTone, setBrandVoiceTone] = useState(tones[0]);
    

    const [targetAudience, setTargetAudience] = useState('');
    const [projectName, setProjectName] = useState('');

    // --- State for Generated Assets ---
    const [aiFullDescription, setAiFullDescription] = useState('');
    const [aiShortTagline, setAiShortTagline] = useState('');
    const [aiRefinedConcept, setAiRefinedConcept] = useState(''); // NEW
    const [aiStrategicKeywords, setAiStrategicKeywords] = useState([]); // NEW
    const [aiPillars, setAiPillars] = useState([]);
    const [aiKeywords, setAiKeywords] = useState([]);

    // --- State for Generation Flow ---
    const [coreAssetsGenerated, setCoreAssetsGenerated] = useState(false);
    const [supportingAssetsGenerated, setSupportingAssetsGenerated] = useState(false);

    // --- State for Image Handling ---
    const [imgSrc, setImgSrc] = useState('');
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState(null);
    const [croppedImageBlob, setCroppedImageBlob] = useState(null);
    const [isCropping, setIsCropping] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);

    // --- State for UI Control ---
    const [isGeneratingCore, setIsGeneratingCore] = useState(false);
    const [isGeneratingSupporting, setIsGeneratingSupporting] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [error, setError] = useState('');
    const [infoMessage, setInfoMessage] = useState('');

    // --- Refs ---
    // --- Refs ---
    const imgRef = useRef(null);
    const fileInputRef = useRef(null);
    const section2Ref = useRef(null);
    const section3Ref = useRef(null);

    // --- Input Validation Helpers ---
    const userKeywordArray = keywordsInput.split(',').map(k => k.trim()).filter(k => k);
    const isUserKeywordsValid = userKeywordArray.length >= MIN_KEYWORDS && userKeywordArray.length <= MAX_KEYWORDS;
    const isShortDescValid = userShortDescription.trim().length >= MIN_SHORT_DESC_LENGTH && userShortDescription.trim().length <= MAX_SHORT_DESC_LENGTH;
    
    const canGenerateCore = platform && industry && isUserKeywordsValid && isShortDescValid && brandVoiceTone && !isGeneratingCore && !isGeneratingSupporting && !isCreating && !isCropping && !isUploading;
    const canGenerateSupporting = coreAssetsGenerated && !supportingAssetsGenerated && aiFullDescription.trim().length >= MIN_SHORT_DESC_LENGTH && !isGeneratingCore && !isGeneratingSupporting && !isCreating && !isCropping && !isUploading;
    const canCreateProject = coreAssetsGenerated && supportingAssetsGenerated && projectName.trim() && !isGeneratingCore && !isGeneratingSupporting && !isCreating && !isCropping && !isUploading;

    // --- Industry typeahead: filter + click-outside ---
    const filteredIndustries = industryQuery.trim()
        ? INDUSTRIES.filter(i => i.toLowerCase().includes(industryQuery.toLowerCase()))
        : INDUSTRIES;

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (industryRef.current && !industryRef.current.contains(e.target)) {
                setIndustryOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);


    // --- Image Helper: Reset State ---
    const resetImageState = useCallback(() => {
        setImgSrc(''); setCroppedImageBlob(null); setCrop(undefined); setCompletedCrop(null);
        setIsCropping(false); setIsUploading(false); setUploadProgress(0);
        setError(''); 
        if (fileInputRef.current) { fileInputRef.current.value = ''; }
    }, []);

    // --- Stable preview URL for cropped blob (avoids re-creating on every render) ---
    const [croppedPreviewUrl, setCroppedPreviewUrl] = useState(null);

    useEffect(() => {
        if (croppedImageBlob) {
            const url = URL.createObjectURL(croppedImageBlob);
            setCroppedPreviewUrl(url);
            return () => URL.revokeObjectURL(url);
        } else {
            setCroppedPreviewUrl(null);
        }
    }, [croppedImageBlob]);

    // --- Revoke blob URL for file input source ---
    useEffect(() => {
        const currentImgSrc = imgSrc;
        return () => {
            if (currentImgSrc && currentImgSrc.startsWith('blob:')) { URL.revokeObjectURL(currentImgSrc); }
        };
    }, [imgSrc]);

    useEffect(() => { 
        setError('');
        setInfoMessage('');
        setCoreAssetsGenerated(false);
        setSupportingAssetsGenerated(false);
        resetImageState();
    }, [platform, industry, keywordsInput, userShortDescription, brandVoiceTone, resetImageState]);


    // --- Other Image Helper Functions ---
    function centerAspectCrop(mediaWidth, mediaHeight, aspect) { return centerCrop( makeAspectCrop({ unit: '%', width: 90, }, aspect, mediaWidth, mediaHeight ), mediaWidth, mediaHeight ); }

    const onFileChange = useCallback((e) => {
        const file = e.target.files?.[0]; if (!file) return;
        setCroppedImageBlob(null); setCompletedCrop(null); setError(''); setUploadProgress(0); setIsUploading(false); setCrop(undefined); setImgSrc('');
        if (file.size > 10 * 1024 * 1024) { setError("File is too large (Max 10MB)."); if (fileInputRef.current) fileInputRef.current.value = ''; return; }
        const newImgSrc = URL.createObjectURL(file); setImgSrc(newImgSrc); setIsCropping(true);
    }, []);

    const onImageLoad = useCallback((e) => {
        const { naturalWidth, naturalHeight } = e.currentTarget;
        if (naturalWidth < MIN_DIMENSION || naturalHeight < MIN_DIMENSION) { setError(`Image too small (Min ${MIN_DIMENSION}x${MIN_DIMENSION}px).`); resetImageState(); return; }
        const { width, height } = e.currentTarget;
        if (!crop) { setCrop(centerAspectCrop(width, height, ASPECT_RATIO)); }
    }, [resetImageState, crop]);

    const handleConfirmCrop = useCallback(async () => {
        if (!completedCrop || !imgRef.current) { setError("Crop area not selected."); return; }
        setError('');
        const image = imgRef.current; if (!(image instanceof HTMLImageElement)) { setError("Image error."); return; }
        const pixelCrop = convertToPixelCrop( completedCrop, image.naturalWidth, image.naturalHeight );
        if (!pixelCrop?.width || !pixelCrop?.height || pixelCrop.width < MIN_DIMENSION || pixelCrop.height < (MIN_DIMENSION / ASPECT_RATIO)) { setError(`Cropped area too small (Min ${MIN_DIMENSION}px wide).`); return; }
        const canvas = document.createElement('canvas'); canvas.width = pixelCrop.width; canvas.height = pixelCrop.height;
        const ctx = canvas.getContext('2d'); if (!ctx) { throw new Error('No 2d context'); }
        const scaleX = image.naturalWidth / image.width; const scaleY = image.naturalHeight / image.height;
        ctx.drawImage( image, pixelCrop.x * scaleX, pixelCrop.y * scaleY, pixelCrop.width * scaleX, pixelCrop.height * scaleY, 0, 0, pixelCrop.width, pixelCrop.height );
        canvas.toBlob((blob) => {
            if (!blob) { setError("Failed to create cropped image."); return; }
            setCroppedImageBlob(blob); setIsCropping(false); setImgSrc('');
        }, 'image/jpeg', 0.9);
    }, [completedCrop]);

   const uploadCoverImage = useCallback((projectId, blob) => { 
       return new Promise((resolve, reject) => {
           if (!userId || !projectId || !blob) { return reject(new Error("Missing user/project ID or image blob.")); }
           setIsUploading(true);
           setUploadProgress(0);
           const storageRef = ref(storage, `projectCovers/${userId}/${projectId}/cover.jpg`);
           const uploadTask = uploadBytesResumable(storageRef, blob, { contentType: 'image/jpeg' });
           uploadTask.on('state_changed',
               (snapshot) => setUploadProgress((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
               (error) => { setError("Image upload failed. Please try again."); setIsUploading(false); setUploadProgress(0); reject(error); },
               async () => {
                   try { const downloadURL = await getDownloadURL(uploadTask.snapshot.ref); setIsUploading(false); setUploadProgress(100); resolve(downloadURL); }
                   catch(err) { setError("Failed to get image URL. Please try again."); setIsUploading(false); reject(err); }
               }
           );
       });
   }, [userId]);

    // --- Action Handlers ---

    /** Step 1: Generate Core Description & Tagline */
    const handleGenerateCoreAssets = async () => {
        if (!canGenerateCore) { setError(`Please ensure all required (*) fields are correctly filled.`); return; }
        setError(''); setInfoMessage('Generating description & tagline...'); setIsGeneratingCore(true);
        setCoreAssetsGenerated(false); setSupportingAssetsGenerated(false);
        setProjectName(''); setAiFullDescription(''); setAiShortTagline(''); setAiRefinedConcept(''); setAiStrategicKeywords([]); setAiPillars([]); setAiKeywords([]);
        resetImageState();

        try {
            const generateCoreFn = httpsCallable(functions, 'generateCoreDescription');
            const result = await generateCoreFn({
                platform,
                industry,
                userKeywords: userKeywordArray, userShortDescription: userShortDescription.trim(),
                brandVoiceTone, targetAudience: targetAudience.trim(),
            });
            if (result.data.success) {
                setAiFullDescription(result.data.fullDescription || '');
                setAiShortTagline(result.data.shortTagline || '');
                setAiRefinedConcept(result.data.refinedConcept || ''); // Capture refined concept
                setAiStrategicKeywords(result.data.strategicKeywords || []); // Capture strategic keywords
                setCoreAssetsGenerated(true);
                setInfoMessage('Description and tagline generated! Review below and generate keywords/pillars.');
                setTimeout(() => section2Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 500);
            } else { throw new Error(result.data.error || "AI core asset generation failed."); }
        } catch (err) {
            setError("Failed to generate brand assets. Please try again."); setInfoMessage('');
            addToast('Failed to generate description and tagline.', 'error');
        } finally {
            setIsGeneratingCore(false);
        }
    };

    /** Step 2: Generate Supporting Keywords & Pillars */
    const handleGenerateSupportingAssets = async () => {
         if (!canGenerateSupporting) { setError("Please ensure core description is generated and valid."); return; }
         setError(''); setInfoMessage('Generating keywords & content pillars...'); setIsGeneratingSupporting(true);
         setSupportingAssetsGenerated(false);
         setAiKeywords([]); setAiPillars([]);

         try {
             const generateSupportingFn = httpsCallable(functions, 'generateSupportingAssets');
             const result = await generateSupportingFn({
                 industry,
                 userKeywords: userKeywordArray,
                 brandVoiceTone,
                 aiFullDescription,
                 targetAudience: targetAudience.trim(),
             });
             if (result.data.success) {
                 setAiKeywords(result.data.aiKeywords || []);
                 setAiPillars(result.data.contentPillars || []);
                 setSupportingAssetsGenerated(true);
                 setInfoMessage('Keywords and pillars generated! Enter project name, add image (optional), and create project.');
                 setTimeout(() => section3Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 500);
             } else { throw new Error(result.data.error || "AI supporting asset generation failed."); }
         } catch (err) {
             setError("Failed to generate brand assets. Please try again."); setInfoMessage('');
             addToast('Failed to generate keywords and pillars.', 'error');
         } finally {
             setIsGeneratingSupporting(false);
         }
     };

    /** Step 3: Create the project document and uploads image */
    const handleCreateProject = async (e) => {
        e.preventDefault();
        if (!canCreateProject) { setError("Please complete all generation steps and provide project name."); return; }
        if (!userId) { setError("Authentication error."); return; }
        setError(''); setInfoMessage('Creating project document...'); setIsCreating(true);

        const defaultVisualSettings = {
            categoryId: "professional",
            vibeId: "smart_auto",
            textStrategy: "auto",
            style: "Professional — Smart Auto"
        };

        const finalProjectData = {
            platform,
            industry,
            userKeywords: userKeywordArray, userShortDescription: userShortDescription.trim(),
            brandVoiceTone, 
            defaultVisualSettings, 
            targetAudience: targetAudience.trim(),
            projectName: projectName.trim(), aiFullDescription, aiShortTagline,
            aiRefinedConcept, aiStrategicKeywords, // Pass refined assets to backend
            aiSuggestedContentPillars: aiPillars,
            aiKeywords: aiKeywords
        };
        let newProjectId = null;

        try {
            const createProjectFn = httpsCallable(functions, 'createEnhancedProject');
            const result = await createProjectFn(finalProjectData);
            newProjectId = result.data?.projectId;
            if (!newProjectId) throw new Error(result.data?.error || "Failed to get project ID after creation.");

            setInfoMessage(`Project document created (ID: ${newProjectId}). Handling cover image...`);

            if (croppedImageBlob) {
                try {
                    setInfoMessage('Uploading cover image...');
                    const imageUrl = await uploadCoverImage(newProjectId, croppedImageBlob);
                    setInfoMessage('Updating project with image URL...');
                    await updateDoc(doc(db, "projects", newProjectId), { coverImageUrl: imageUrl, updatedAt: serverTimestamp() });
                    setInfoMessage('Project and cover image saved successfully! Redirecting...');
                } catch (uploadError) {
                    setError(`Project created, but cover image failed: ${uploadError.message}. You can add it later.`);
                    setInfoMessage('');
                    addToast('Cover image upload failed. You can add it later.', 'warning');
                }
            } else {
                 setInfoMessage('Project created successfully! Redirecting...');
            }

             setTimeout(() => { navigate(newProjectId ? `/project/${newProjectId}` : '/projects'); }, 1500);

        } catch (err) {
            setError("Project creation failed. Please try again.");
            setInfoMessage('');
            addToast('Failed to create project.', 'error');
            setIsCreating(false);
        }
    };

    // --- Render Component ---
    return (
        <div className="space-y-8 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-10 pb-32">
             {/* Header */}
            <div className="flex flex-col gap-4 border-b border-white/5 pb-8">
                <div>
                     <div className="flex items-center gap-2 mb-2">
                         <span className="px-3 py-1 bg-slate-blue/10 rounded-full border border-slate-blue/20 text-[10px] uppercase font-black tracking-widest text-slate-blue">
                             New Project
                         </span>
                     </div>
                    <h1 className="text-5xl font-black text-cream uppercase tracking-tighter drop-shadow-lg">
                        Ignition <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-blue to-cream">Sequence</span>
                    </h1>
                    <p className="text-cream/50 mt-2 font-medium text-lg">Create Your Project — Define your brand and let AI build your content engine.</p>
                </div>
            </div>

            {/* Feedback Area */}
            <div className="min-h-[40px] text-center space-y-2">
                {error && <p className="text-sm text-soft-coral bg-soft-coral/10 border border-soft-coral/20 px-4 py-3 rounded-2xl flex items-center justify-center font-bold animate-fade-in shadow-lg"><FiAlertTriangle className="mr-2" />{error}</p>}
                {infoMessage && <p className="text-sm text-slate-blue bg-slate-blue/10 border border-slate-blue/20 px-4 py-3 rounded-2xl flex items-center justify-center font-bold animate-fade-in shadow-lg"><FiInfo className="mr-2" />{infoMessage}</p>}
            </div>

            <form onSubmit={handleCreateProject} className="space-y-12">

                {/* --- Section 1: Define Project Focus & Generate Core --- */}
                {/* --- Section 1: Define Project Focus & Generate Core --- */}
                <section className="relative p-10 bg-warm-gray/10 backdrop-blur-md rounded-[3rem] border border-white/5 shadow-2xl overflow-hidden group">
                     {/* Ambient Glow Removed */}
                     <div className="absolute top-0 right-0 p-10 text-[180px] font-black text-white/5 leading-none select-none pointer-events-none">1</div>
                     
                     <div className="relative z-10 space-y-8">
                         <h2 className="text-2xl font-black text-white uppercase tracking-tight flex items-center gap-3">
                             <div className="w-8 h-8 rounded-full bg-slate-blue text-white flex items-center justify-center text-sm font-bold shadow-glow-sm">1</div>
                             Define Project Focus
                         </h2>
                         
                        {/* Platform & Industry */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-3">
                                <label htmlFor="platform" className="block text-xs font-black text-cream/40 uppercase tracking-widest">Platform <span className="text-soft-coral">*</span></label>
                                <select id="platform" value={platform} onChange={(e) => setPlatform(e.target.value)} required disabled={isGeneratingCore || isGeneratingSupporting || isCreating || isCropping || isUploading || coreAssetsGenerated} className="block w-full bg-black/20 border border-white/5 rounded-2xl text-cream focus:border-slate-blue focus:ring-1 focus:ring-slate-blue py-4 px-6 appearance-none font-medium hover:bg-black/30 transition-colors cursor-pointer">
                                    {platforms.map(p => <option key={p} value={p} className="bg-warm-dark text-cream">{p}</option>)}
                                </select>
                            </div>
                            <div className="space-y-3 relative" ref={industryRef}>
                                <label htmlFor="industry" className="block text-xs font-black text-cream/40 uppercase tracking-widest">Industry <span className="text-soft-coral">*</span></label>
                                <div className="relative">
                                    <input
                                        id="industry"
                                        type="text"
                                        value={industryQuery}
                                        onChange={(e) => {
                                            setIndustryQuery(e.target.value);
                                            setIndustry('');
                                            setIndustryOpen(true);
                                        }}
                                        onFocus={() => setIndustryOpen(true)}
                                        placeholder="Type to search industries..."
                                        disabled={isGeneratingCore || isGeneratingSupporting || isCreating || isCropping || isUploading || coreAssetsGenerated}
                                        className="block w-full bg-black/20 border border-white/5 rounded-2xl text-cream placeholder-cream/20 focus:border-slate-blue focus:ring-1 focus:ring-slate-blue py-4 px-6 font-medium hover:bg-black/30 transition-colors"
                                    />
                                    {industry && (
                                        <button
                                            type="button"
                                            onClick={() => { setIndustry(''); setIndustryQuery(''); }}
                                            disabled={isGeneratingCore || isGeneratingSupporting || isCreating || isCropping || isUploading || coreAssetsGenerated}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-cream/30 hover:text-cream text-sm font-bold"
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>
                                {industry && (
                                    <p className="text-xs text-muted-sage font-bold">Selected: {industry}</p>
                                )}
                                {industryOpen && !industry && filteredIndustries.length > 0 && (
                                    <div className="absolute z-50 top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-warm-gray border border-white/10 rounded-2xl shadow-modal">
                                        {filteredIndustries.map((ind) => (
                                            <button
                                                key={ind}
                                                type="button"
                                                onClick={() => {
                                                    setIndustry(ind);
                                                    setIndustryQuery(ind);
                                                    setIndustryOpen(false);
                                                }}
                                                className="w-full text-left px-5 py-3 text-sm text-cream/80 hover:bg-slate-blue/15 hover:text-cream transition-colors first:rounded-t-2xl last:rounded-b-2xl"
                                            >
                                                {ind}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {industryOpen && !industry && filteredIndustries.length === 0 && industryQuery.trim() && (
                                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-warm-gray border border-white/10 rounded-2xl shadow-modal p-4">
                                        <p className="text-xs text-cream/40 text-center">No matching industries found</p>
                                    </div>
                                )}
                            </div>
                        </div>
                         
                         {/* Keywords */}
                        <div className="space-y-3">
                            <label htmlFor="keywords" className="block text-xs font-black text-cream/40 uppercase tracking-widest">
                                Your Keywords ({userKeywordArray.length} / {MIN_KEYWORDS}-{MAX_KEYWORDS}) <span className="text-soft-coral">*</span>
                            </label>
                            <textarea id="keywords" rows="2" value={keywordsInput} onChange={(e) => setKeywordsInput(e.target.value)} required disabled={isGeneratingCore || isGeneratingSupporting || isCreating || isCropping || isUploading || coreAssetsGenerated} className="block w-full bg-black/20 border border-white/5 rounded-2xl text-cream placeholder-cream/20 focus:border-slate-blue focus:ring-1 focus:ring-slate-blue py-4 px-6 font-medium shadow-inner hover:bg-black/30 transition-colors" placeholder={`Enter ${MIN_KEYWORDS}-${MAX_KEYWORDS} keywords, separated by commas...`} />
                            {!isUserKeywordsValid && keywordsInput && <p className="text-xs text-soft-coral font-bold flex items-center"><FiAlertTriangle className="mr-1"/> Enter between {MIN_KEYWORDS} and {MAX_KEYWORDS} keywords.</p>}
                            <p className="text-[10px] text-cream/30 uppercase tracking-wider font-bold">Provide initial keywords. AI will generate refined content pillars later.</p>
                        </div>
                        
                         {/* Short Description */}
                        <div className="space-y-3">
                            <label htmlFor="shortDescription" className="block text-xs font-black text-cream/40 uppercase tracking-widest">
                                Short Project Description ({userShortDescription.trim().length}/{MAX_SHORT_DESC_LENGTH}) <span className="text-soft-coral">*</span>
                            </label>
                            <textarea id="shortDescription" rows="3" value={userShortDescription} onChange={(e) => setUserShortDescription(e.target.value)} required minLength={MIN_SHORT_DESC_LENGTH} maxLength={MAX_SHORT_DESC_LENGTH} disabled={isGeneratingCore || isGeneratingSupporting || isCreating || isCropping || isUploading || coreAssetsGenerated} className="block w-full bg-black/20 border border-white/5 rounded-2xl text-cream placeholder-cream/20 focus:border-slate-blue focus:ring-1 focus:ring-slate-blue py-4 px-6 font-medium shadow-inner hover:bg-black/30 transition-colors" placeholder={`Briefly describe the project's core idea, mission, or aesthetic...`} />
                            {!isShortDescValid && userShortDescription && <p className="text-xs text-soft-coral font-bold flex items-center"><FiAlertTriangle className="mr-1"/> Description must be {MIN_SHORT_DESC_LENGTH}-{MAX_SHORT_DESC_LENGTH} characters.</p>}
                        </div>
                        
                         {/* Tone */}
                        <div className="space-y-3">
                            <label htmlFor="tone" className="block text-xs font-black text-cream/40 uppercase tracking-widest">Brand Voice / Tone <span className="text-soft-coral">*</span></label>
                            <select id="tone" value={brandVoiceTone} onChange={(e) => setBrandVoiceTone(e.target.value)} required disabled={isGeneratingCore || isGeneratingSupporting || isCreating || isCropping || isUploading || coreAssetsGenerated} className="block w-full bg-black/20 border border-white/5 rounded-2xl text-cream focus:border-slate-blue focus:ring-1 focus:ring-slate-blue py-4 px-6 appearance-none font-medium hover:bg-black/30 transition-colors cursor-pointer">
                                {tones.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                        
                        {/* Optional Fields (Audience Only) */}
                        <div className="space-y-3">
                             <label htmlFor="audience" className="block text-xs font-black text-cream/40 uppercase tracking-widest">Target Audience (Optional)</label>
                             <textarea id="audience" rows="2" value={targetAudience} onChange={(e) => setTargetAudience(e.target.value)} disabled={isGeneratingCore || isGeneratingSupporting || isCreating || isCropping || isUploading || coreAssetsGenerated} className="block w-full bg-black/20 border border-white/5 rounded-2xl text-cream placeholder-cream/20 focus:border-slate-blue focus:ring-1 focus:ring-slate-blue py-4 px-6 font-medium shadow-inner hover:bg-black/30 transition-colors" placeholder="e.g., Busy moms, Tech startups..." />
                             <p className="text-[10px] text-cream/30 uppercase tracking-wider font-bold">Describe your ideal customer or follower.</p>
                        </div>
                        
                         {/* Generate CORE Button */}
                         <div className="pt-6">
                             <button type="button" onClick={handleGenerateCoreAssets} disabled={!canGenerateCore || coreAssetsGenerated} className="w-full btn btn-primary py-5 rounded-2xl shadow-glow text-sm uppercase tracking-widest font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.01] active:scale-[0.99] transition-all">
                                 <div className="flex items-center justify-center">
                                     <FiRefreshCw className={`mr-3 h-5 w-5 ${isGeneratingCore ? 'animate-spin' : ''}`} />
                                     {isGeneratingCore ? 'Generating Descriptions...' : 'Generate Descriptions & Tagline'}
                                 </div>
                             </button>
                         </div>
                    </div>
                </section>

                {/* --- Section 2: Review Core & Generate Supporting --- */}
                {coreAssetsGenerated && (
                    <section ref={section2Ref} className="relative p-10 bg-warm-gray/10 backdrop-blur-md rounded-[3rem] border border-white/5 shadow-2xl overflow-hidden group animate-fade-in-up">
                        <div className="absolute top-10 left-10 text-[180px] font-black text-white/5 leading-none select-none pointer-events-none">2</div>
                        
                        <div className="relative z-10 space-y-8">
                             <h2 className="text-2xl font-black text-white uppercase tracking-tight flex items-center gap-3">
                                 <div className="w-8 h-8 rounded-full bg-slate-blue text-white flex items-center justify-center text-sm font-bold shadow-glow-sm">2</div>
                                 Review & Expand
                             </h2>

                            {/* Descriptions (Editable) */}
                            <div className="space-y-3">
                                <label htmlFor="aiFullDescription" className="block text-xs font-black text-cream/40 uppercase tracking-widest">AI-Generated Vision (Editable)</label>
                                <textarea id="aiFullDescription" rows="4" value={aiFullDescription} onChange={(e) => setAiFullDescription(e.target.value)} disabled={isGeneratingSupporting || isCreating || isUploading || supportingAssetsGenerated} className="block w-full bg-black/20 border border-white/5 rounded-2xl text-cream placeholder-cream/20 focus:border-slate-blue focus:ring-1 focus:ring-slate-blue py-4 px-6 font-medium shadow-inner leading-relaxed" />
                            </div>
                             <div className="space-y-3">
                                <label htmlFor="aiShortTagline" className="block text-xs font-black text-cream/40 uppercase tracking-widest">AI-Generated Tagline (Editable)</label>
                                <input id="aiShortTagline" type="text" value={aiShortTagline} onChange={(e) => setAiShortTagline(e.target.value)} disabled={isGeneratingSupporting || isCreating || isUploading || supportingAssetsGenerated} className="block w-full bg-black/20 border border-white/5 rounded-2xl text-cream placeholder-cream/20 focus:border-slate-blue focus:ring-1 focus:ring-slate-blue py-4 px-6 font-medium shadow-inner" />
                            </div>

                             {/* Generate SUPPORTING Button */}
                             <div className="pt-6">
                                <button type="button" onClick={handleGenerateSupportingAssets} disabled={!canGenerateSupporting || supportingAssetsGenerated} className="w-full btn btn-primary py-5 rounded-2xl shadow-glow text-sm uppercase tracking-widest font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.01] active:scale-[0.99] transition-all">
                                    <div className="flex items-center justify-center">
                                        <FiCpu className={`mr-3 h-5 w-5 ${isGeneratingSupporting ? 'animate-spin' : ''}`} />
                                        {isGeneratingSupporting ? 'Analyzing Industry...' : 'Generate Content Strategy'}
                                    </div>
                                </button>
                            </div>
                        </div>
                    </section>
                )}

                {/* --- Section 3: Review Supporting, Name & Finalize --- */}
                {coreAssetsGenerated && supportingAssetsGenerated && (
                     <section ref={section3Ref} className="relative p-10 bg-warm-gray/10 backdrop-blur-md rounded-[3rem] border border-white/5 shadow-2xl overflow-hidden group animate-fade-in-up">
                         <div className="absolute top-10 left-10 text-[180px] font-black text-white/5 leading-none select-none pointer-events-none">3</div>
                         
                         <div className="relative z-10 space-y-8">
                             <h2 className="text-2xl font-black text-white uppercase tracking-tight flex items-center gap-3">
                                 <div className="w-8 h-8 rounded-full bg-slate-blue text-white flex items-center justify-center text-sm font-bold shadow-glow-sm">3</div>
                                 Finalize Launch
                             </h2>

                             {/* Display AI Keywords & Pillars (Read-only) */}
                             <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                  <div className="bg-black/20 rounded-2xl p-6 border border-white/5">
                                    <h4 className="text-xs font-black text-cream/40 uppercase tracking-widest mb-4 flex items-center"><FiTag className="mr-2 text-slate-blue"/> AI Generated Keywords</h4>
                                    {aiKeywords.length > 0 ? (
                                        <div className="flex flex-wrap gap-2">
                                            {aiKeywords.map((kw, i) => (<span key={`ai-kw-${i}`} className="bg-slate-blue/10 text-slate-blue border border-slate-blue/20 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide">{kw}</span>))}
                                        </div>
                                    ) : <p className="text-sm text-cream/30 italic">None generated.</p>}
                                 </div>
                                 <div className="bg-black/20 rounded-2xl p-6 border border-white/5">
                                    <h4 className="text-xs font-black text-cream/40 uppercase tracking-widest mb-4 flex items-center"><FiList className="mr-2 text-slate-blue"/> Content Pillars</h4>
                                    {aiPillars.length > 0 ? (
                                        <ul className="space-y-4">
                                            {aiPillars.map((pillar, index) => {
                                                const isStructured = typeof pillar === 'object' && pillar.name;
                                                return (
                                                    <li key={index} className="flex items-start gap-3">
                                                        <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-slate-blue flex-shrink-0"></div>
                                                        {isStructured ? (
                                                            <div className="space-y-0.5">
                                                                <span className="text-sm text-cream font-bold leading-relaxed">{pillar.name}</span>
                                                                {pillar.description && <p className="text-xs text-cream/50 leading-relaxed">{pillar.description}</p>}
                                                                {pillar.exampleTopic && <p className="text-xs text-slate-blue/60 italic">e.g. {pillar.exampleTopic}</p>}
                                                            </div>
                                                        ) : (
                                                            <span className="text-sm text-cream/80 font-medium leading-relaxed">{pillar}</span>
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    ) : <p className="text-sm text-cream/30 italic">None generated.</p>}
                                 </div>
                             </div>

                             {/* Project Name Input */}
                             <div className="pt-8 border-t border-white/5">
                                <label htmlFor="projectName" className="block text-xs font-black text-cream/40 uppercase tracking-widest mb-3">Project Name <span className="text-soft-coral">*</span></label>
                                <input id="projectName" type="text" value={projectName} onChange={(e) => setProjectName(e.target.value)} required maxLength={PROJECT_NAME_MAX_LENGTH} disabled={isCreating || isUploading} className="block w-full bg-black/20 border border-white/5 rounded-2xl text-cream placeholder-cream/20 focus:border-slate-blue focus:ring-1 focus:ring-slate-blue py-4 px-6 font-bold text-lg shadow-inner" placeholder="Enter a final name for this project..."/>
                             </div>

                             {/* Cover Image Section */}
                             <div className="pt-8 border-t border-white/5">
                                 <label className="block text-xs font-black text-cream/40 uppercase tracking-widest mb-4">Cover Image (Optional)</label>
                                 <input type="file" accept="image/png, image/jpeg, image/webp" onChange={onFileChange} className="hidden" ref={fileInputRef} id="cover-image-input" disabled={isCreating || isGeneratingCore || isGeneratingSupporting || isUploading || isCropping}/>
                                {!imgSrc && !croppedImageBlob && (
                                    <button type="button" onClick={() => fileInputRef.current?.click()} disabled={isCreating || isGeneratingCore || isGeneratingSupporting || isUploading || isCropping} className="w-full flex justify-center items-center py-12 border-2 border-white/5 border-dashed rounded-3xl text-sm font-bold text-cream/40 hover:border-slate-blue/50 hover:text-slate-blue hover:bg-slate-blue/5 transition-all disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wider">
                                        <FiUploadCloud className="mr-3 h-6 w-6" /> Upload Cover Image (16:9)
                                    </button>
                                )}
                                {imgSrc && isCropping && (
                                    <div className="space-y-4 p-6 border border-white/10 rounded-3xl bg-black/40 backdrop-blur-md">
                                        <p className="text-xs font-bold text-cream/60 uppercase tracking-wider">Adjust crop area (16:9)</p>
                                        <ReactCrop crop={crop} onChange={(_, percentCrop) => setCrop(percentCrop)} onComplete={(c) => setCompletedCrop(c)} aspect={ASPECT_RATIO} minWidth={MIN_DIMENSION} className="max-w-full border border-white/10 rounded-xl overflow-hidden shadow-2xl">
                                            <img ref={imgRef} src={imgSrc} alt="Crop preview" style={{ maxHeight: "500px", width: "auto", display: 'block' }} onLoad={onImageLoad}/>
                                        </ReactCrop>
                                        <div className="flex justify-end gap-3 pt-2">
                                            <button type="button" onClick={resetImageState} disabled={isCreating || isUploading} className="px-6 py-3 border border-white/10 rounded-xl text-xs font-bold uppercase tracking-wider text-cream/60 bg-white/5 hover:bg-white/10 transition-colors"> Cancel </button>
                                            <button type="button" onClick={handleConfirmCrop} disabled={!completedCrop || isCreating || isUploading} className="px-6 py-3 bg-slate-blue hover:bg-slate-blue/80 rounded-xl text-xs font-bold uppercase tracking-wider text-white shadow-glow transition-colors"> Confirm Crop </button>
                                        </div>
                                    </div>
                                )}
                                {croppedImageBlob && !isCropping && (
                                    <div className="space-y-4">
                                        <div className="relative aspect-w-16 aspect-h-9 overflow-hidden rounded-3xl border border-white/10 bg-black/20 max-w-lg shadow-2xl group/preview">
                                            <img key={croppedPreviewUrl || 'no-blob'} src={croppedPreviewUrl || ''} alt="Cropped Cover" className="object-cover w-full h-full"/>
                                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover/preview:opacity-100 transition-opacity flex items-center justify-center">
                                                <button type="button" onClick={resetImageState} disabled={isCreating || isUploading} className="btn btn-secondary text-xs uppercase font-bold tracking-wider"> Change Image </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {isUploading && (
                                   <div className="mt-4 max-w-md">
                                     <div className="flex justify-between text-xs font-bold uppercase tracking-wider text-cream/40 mb-2">
                                         <span>Uploading Image...</span>
                                         <span>{Math.round(uploadProgress)}%</span>
                                     </div>
                                     <div className="w-full bg-white/5 rounded-full h-1 overflow-hidden"> 
                                        <div className="bg-slate-blue h-full transition-all duration-300 ease-out shadow-glow" style={{ width: `${uploadProgress}%` }}></div> 
                                     </div>
                                   </div>
                                )}
                             </div>

                             {/* Final Create Project Button */}
                            <div className="pt-10 border-t border-white/5">
                                <div className="flex flex-col sm:flex-row-reverse gap-4">
                                    <button type="submit" disabled={!canCreateProject} className="w-full sm:w-auto btn btn-primary py-4 px-10 rounded-full shadow-glow text-sm uppercase tracking-widest font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] active:scale-[0.98] transition-all">
                                        {isCreating ? (isUploading ? 'Uploading & Creating...' : 'Creating Project...') : 'Launch Project'}
                                    </button>
                                    <button type="button" onClick={() => navigate('/projects')} disabled={isCreating || isGeneratingCore || isGeneratingSupporting || isUploading || isCropping} className="w-full sm:w-auto py-4 px-8 border border-white/10 rounded-full text-sm font-bold uppercase tracking-widest text-cream/40 hover:text-white hover:bg-white/5 transition-all disabled:opacity-50">
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                 {/* Optional: Only show cancel if nothing generated */}
                 {!coreAssetsGenerated && (
                     <div className="pt-6">
                         <button type="button" onClick={() => navigate('/projects')} disabled={isCreating || isGeneratingCore || isGeneratingSupporting || isUploading || isCropping} className="w-full sm:w-auto py-4 px-8 border border-white/10 rounded-full text-sm font-bold uppercase tracking-widest text-cream/40 hover:text-white hover:bg-white/5 transition-all disabled:opacity-50">
                             Cancel Initialization
                         </button>
                     </div>
                 )}
            </form>
        </div>
    );
}

export default NewProject;